import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  Briefcase,
  Building2,
  Check,
  FileText,
  GraduationCap,
  Loader,
  Pencil,
  Plus,
  Save,
  Shield,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import {
  expertProfileApi,
  ExpertCredentials,
  EMPTY_CREDENTIALS,
} from '../services/expertProfileApi';

type FormState = ExpertCredentials;

const TITLE_OPTIONS = ['Dr', 'Prof', 'Mr', 'Mrs', 'Ms', 'Miss'];
const REGISTRATION_BODY_OPTIONS = ['GMC', 'NMC', 'HCPC', 'SRA', 'GDC', 'GOC', 'GPhC', 'Other'];

export const ProfessionalProfilePage: React.FC = () => {
  const [form, setForm] = useState<FormState>({ ...EMPTY_CREDENTIALS });
  const [cvLastParsedAt, setCvLastParsedAt] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dirty, setDirty] = useState(false);
  const [hasProfile, setHasProfile] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await expertProfileApi.get();
      if (data.credentials && Object.keys(data.credentials).length > 0) {
        setForm({
          ...EMPTY_CREDENTIALS,
          ...data.credentials,
          specialisations: data.credentials.specialisations || [],
          professionalMemberships: data.credentials.professionalMemberships || [],
          notableAppointments: data.credentials.notableAppointments || [],
        });
        setHasProfile(true);
      }
      setCvLastParsedAt(data.cvLastParsedAt);
    } catch {
      // No profile yet — that's fine
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      await expertProfileApi.save(form);
      setSuccess('Professional profile saved successfully.');
      setDirty(false);
      setHasProfile(true);
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setDeleting(true);
      setError('');
      await expertProfileApi.delete();
      setForm({ ...EMPTY_CREDENTIALS });
      setCvLastParsedAt(null);
      setHasProfile(false);
      setDirty(false);
      setShowDeleteConfirm(false);
      setSuccess('Professional profile deleted successfully.');
      setTimeout(() => setSuccess(''), 4000);
    } catch (e: any) {
      setError(e.message || 'Failed to delete profile');
    } finally {
      setDeleting(false);
    }
  };

  const handleFileUpload = async (file: File) => {
    const lower = file.name.toLowerCase();
    if (!lower.endsWith('.pdf') && !lower.endsWith('.docx')) {
      setError('Please upload a PDF or DOCX file.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10 MB.');
      return;
    }

    try {
      setParsing(true);
      setError('');
      setSuccess('');
      const result = await expertProfileApi.parseCv(file);
      if (result.credentials) {
        setForm({
          ...EMPTY_CREDENTIALS,
          ...result.credentials,
          specialisations: result.credentials.specialisations || [],
          professionalMemberships: result.credentials.professionalMemberships || [],
          notableAppointments: result.credentials.notableAppointments || [],
        });
        setDirty(true);
        setSuccess('Credentials extracted successfully. Please review the fields below and save.');
        setTimeout(() => setSuccess(''), 6000);
      }
    } catch (e: any) {
      setError(e.message || 'Failed to parse CV');
    } finally {
      setParsing(false);
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFileUpload(file);
  }, []);

  const updateField = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  const addToArray = (key: 'specialisations' | 'professionalMemberships' | 'notableAppointments', value: string) => {
    if (!value.trim()) return;
    setForm(prev => ({
      ...prev,
      [key]: [...(prev[key] || []), value.trim()],
    }));
    setDirty(true);
  };

  const removeFromArray = (key: 'specialisations' | 'professionalMemberships' | 'notableAppointments', index: number) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].filter((_, i) => i !== index),
    }));
    setDirty(true);
  };

  const editInArray = (key: 'specialisations' | 'professionalMemberships' | 'notableAppointments', index: number, value: string) => {
    setForm(prev => ({
      ...prev,
      [key]: prev[key].map((item, i) => i === index ? value : item),
    }));
    setDirty(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader className="h-6 w-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-medical-900 dark:text-medical-100 mb-2">
            Professional Profile
          </h1>
          <p className="text-medical-600 dark:text-medical-400">
            Manage your professional credentials for report population
          </p>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            These credentials are used to populate your expert witness reports.
            They are not verified by ClinLexis and do not constitute a credentials check.
          </p>
        </div>

        {/* Error / Success */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-700 rounded-lg p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-emerald-800 dark:text-emerald-300">{success}</p>
          </div>
        )}

        {/* CV Upload */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Upload className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Upload Document
            </h2>
          </div>
          <p className="text-sm text-medical-500 dark:text-medical-400 mb-4">
            Upload a CV or report (PDF/DOCX) and we'll extract your professional credentials automatically.
          </p>

          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              parsing
                ? 'border-primary-300 bg-primary-50 dark:bg-primary-900/10'
                : 'border-medical-300 dark:border-medical-600 hover:border-primary-400 cursor-pointer'
            }`}
            onDragOver={e => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => !parsing && fileInputRef.current?.click()}
          >
            {parsing ? (
              <div className="flex flex-col items-center gap-3">
                <Loader className="h-8 w-8 animate-spin text-primary-500" />
                <p className="text-sm text-medical-600 dark:text-medical-400">
                  Extracting your credentials... This may take a moment.
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <FileText className="h-8 w-8 text-medical-400" />
                <p className="text-sm text-medical-600 dark:text-medical-400">
                  Drag and drop your document here, or click to browse
                </p>
                <p className="text-xs text-medical-400">PDF or DOCX, max 10 MB</p>
              </div>
            )}
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            className="hidden"
            onChange={e => {
              const file = e.target.files?.[0];
              if (file) handleFileUpload(file);
              e.target.value = '';
            }}
          />

          {cvLastParsedAt && (
            <p className="text-xs text-medical-400 dark:text-medical-500 mt-3">
              Last extracted from document: {new Date(cvLastParsedAt).toLocaleDateString('en-GB', {
                day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </p>
          )}
        </div>

        {/* Personal Details */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Personal Details
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <DropdownField
              label="Title"
              value={form.title || ''}
              options={TITLE_OPTIONS}
              onChange={v => updateField('title', v || null)}
            />
            <TextField
              label="Full Name"
              value={form.fullName || ''}
              onChange={v => updateField('fullName', v || null)}
              className="md:col-span-2"
            />
            <TextField
              label="Post-nominal Letters"
              value={form.postNominals || ''}
              onChange={v => updateField('postNominals', v || null)}
              placeholder="e.g. MBBS, FRCS(Orth), PhD"
              className="md:col-span-3"
            />
          </div>
        </div>

        {/* Registration */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Shield className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Professional Registration
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <DropdownField
              label="Registration Body"
              value={form.registrationBody || ''}
              options={REGISTRATION_BODY_OPTIONS}
              onChange={v => updateField('registrationBody', v || null)}
            />
            <TextField
              label="Registration Number"
              value={form.registrationNumber || ''}
              onChange={v => updateField('registrationNumber', v || null)}
            />
          </div>
        </div>

        {/* Position */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Building2 className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Current Position
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextField
              label="Position / Role"
              value={form.currentPosition || ''}
              onChange={v => updateField('currentPosition', v || null)}
            />
            <TextField
              label="Employer / Organisation"
              value={form.employer || ''}
              onChange={v => updateField('employer', v || null)}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            <NumberField
              label="Years of Experience"
              value={form.yearsOfExperience}
              onChange={v => updateField('yearsOfExperience', v)}
            />
          </div>
        </div>

        {/* Specialisations */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <GraduationCap className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Expertise & Memberships
            </h2>
          </div>

          <TagField
            label="Specialisations"
            items={form.specialisations}
            onAdd={v => addToArray('specialisations', v)}
            onRemove={i => removeFromArray('specialisations', i)}
            onEdit={(i, v) => editInArray('specialisations', i, v)}
            placeholder="Add a specialisation"
          />

          <div className="mt-4">
            <TagField
              label="Professional Memberships"
              items={form.professionalMemberships}
              onAdd={v => addToArray('professionalMemberships', v)}
              onRemove={i => removeFromArray('professionalMemberships', i)}
              onEdit={(i, v) => editInArray('professionalMemberships', i, v)}
              placeholder="Add a membership"
            />
          </div>

          <div className="mt-4">
            <TagField
              label="Notable Appointments"
              items={form.notableAppointments}
              onAdd={v => addToArray('notableAppointments', v)}
              onRemove={i => removeFromArray('notableAppointments', i)}
              onEdit={(i, v) => editInArray('notableAppointments', i, v)}
              placeholder="Add an appointment"
            />
          </div>
        </div>

        {/* Free Text Summaries */}
        <div className="bg-white dark:bg-medical-800 rounded-lg shadow-sm border border-medical-200 dark:border-medical-700 p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-slate-600 dark:text-slate-400" />
            <h2 className="text-xl font-semibold text-medical-900 dark:text-medical-100">
              Summaries
            </h2>
          </div>

          <TextAreaField
            label="Qualifications Summary"
            value={form.qualificationsSummary || ''}
            onChange={v => updateField('qualificationsSummary', v || null)}
            placeholder="Brief summary of your qualifications and training"
          />

          <div className="mt-4">
            <TextAreaField
              label="Expert Witness Experience"
              value={form.expertWitnessStatement || ''}
              onChange={v => updateField('expertWitnessStatement', v || null)}
              placeholder="Summary of your expert witness experience"
            />
          </div>

          <div className="mt-4">
            <TextAreaField
              label="Publications Summary"
              value={form.publicationsSummary || ''}
              onChange={v => updateField('publicationsSummary', v || null)}
              placeholder="Brief summary of publications if applicable"
            />
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center">
          {hasProfile ? (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg font-medium transition-colors"
            >
              <Trash2 className="h-4 w-4" />
              Delete Profile
            </button>
          ) : (
            <div />
          )}
          <button
            onClick={handleSave}
            disabled={saving || !dirty}
            className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <Loader className="h-4 w-4 animate-spin" />
            ) : !dirty && hasProfile ? (
              <Check className="h-4 w-4" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {saving ? 'Saving...' : !dirty && hasProfile ? 'Profile Saved' : 'Save Profile'}
          </button>
        </div>

        {/* Delete confirmation dialog */}
        {showDeleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white dark:bg-medical-800 rounded-lg shadow-xl border border-medical-200 dark:border-medical-700 p-6 max-w-md mx-4">
              <h3 className="text-lg font-semibold text-medical-900 dark:text-medical-100 mb-2">
                Delete Professional Profile?
              </h3>
              <p className="text-sm text-medical-600 dark:text-medical-400 mb-6">
                This will permanently remove all your professional credentials. Future reports will no longer include your profile details.
              </p>
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  disabled={deleting}
                  className="px-4 py-2 text-sm font-medium text-medical-700 dark:text-medical-300 hover:bg-medical-100 dark:hover:bg-medical-700 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors disabled:opacity-50"
                >
                  {deleting ? (
                    <Loader className="h-4 w-4 animate-spin" />
                  ) : (
                    <Trash2 className="h-4 w-4" />
                  )}
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Reusable form field components ──────────────────────────────────────────

const fieldClasses =
  'w-full rounded-md border border-medical-300 dark:border-medical-600 bg-white dark:bg-medical-700 px-3 py-2 text-sm text-medical-900 dark:text-medical-100 placeholder-medical-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent';

function TextField({
  label, value, onChange, placeholder, className,
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={fieldClasses} />
    </div>
  );
}

function NumberField({
  label, value, onChange,
}: {
  label: string; value: number | null; onChange: (v: number | null) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">{label}</label>
      <input
        type="number"
        min={0}
        max={80}
        value={value ?? ''}
        onChange={e => onChange(e.target.value ? parseInt(e.target.value, 10) : null)}
        className={fieldClasses}
      />
    </div>
  );
}

function DropdownField({
  label, value, options, onChange,
}: {
  label: string; value: string; options: string[]; onChange: (v: string) => void;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">{label}</label>
      <select value={value} onChange={e => onChange(e.target.value)} className={fieldClasses}>
        <option value="">Select...</option>
        {options.map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );
}

function TextAreaField({
  label, value, onChange, placeholder,
}: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string;
}) {
  return (
    <div>
      <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={fieldClasses + ' resize-y'}
      />
    </div>
  );
}

function TagField({
  label, items, onAdd, onRemove, onEdit, placeholder,
}: {
  label: string; items: string[]; onAdd: (v: string) => void; onRemove: (i: number) => void; onEdit: (i: number, v: string) => void; placeholder?: string;
}) {
  const [input, setInput] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const editRef = useRef<HTMLInputElement>(null);

  const handleAdd = () => {
    if (input.trim()) {
      onAdd(input.trim());
      setInput('');
    }
  };

  const startEditing = (i: number) => {
    setEditingIndex(i);
    setEditValue(items[i]);
    setTimeout(() => editRef.current?.focus(), 0);
  };

  const commitEdit = () => {
    if (editingIndex !== null && editValue.trim()) {
      onEdit(editingIndex, editValue.trim());
    }
    setEditingIndex(null);
    setEditValue('');
  };

  return (
    <div>
      <label className="block text-sm font-medium text-medical-700 dark:text-medical-300 mb-1">{label}</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {items.map((item, i) => (
          editingIndex === i ? (
            <span key={i} className="inline-flex items-center gap-1 rounded-full border border-primary-400 dark:border-primary-500 bg-white dark:bg-medical-700 overflow-hidden">
              <input
                ref={editRef}
                type="text"
                value={editValue}
                onChange={e => setEditValue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') { e.preventDefault(); commitEdit(); }
                  if (e.key === 'Escape') { setEditingIndex(null); setEditValue(''); }
                }}
                onBlur={commitEdit}
                className="px-2.5 py-1 text-sm bg-transparent text-medical-900 dark:text-medical-100 focus:outline-none min-w-[100px]"
              />
              <button onClick={commitEdit} className="pr-2 text-primary-600 hover:text-primary-700 transition-colors">
                <Check className="h-3 w-3" />
              </button>
            </span>
          ) : (
            <span
              key={i}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-sm bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-700"
            >
              {item}
              <button
                onClick={() => startEditing(i)}
                className="hover:text-primary-900 dark:hover:text-primary-100 transition-colors"
                title="Edit"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                onClick={() => onRemove(i)}
                className="hover:text-red-500 transition-colors"
                title="Remove"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          )
        ))}
      </div>
      <div className="flex gap-2">
        <input
          type="text"
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAdd();
            }
          }}
          placeholder={placeholder}
          className={fieldClasses + ' flex-1'}
        />
        <button
          onClick={handleAdd}
          className="px-3 py-2 bg-medical-100 dark:bg-medical-700 hover:bg-medical-200 dark:hover:bg-medical-600 rounded-md text-medical-700 dark:text-medical-300 transition-colors"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
